import { DOCUMENT } from '@angular/common';
import { HttpEvent, HttpResponse } from '@angular/common/http';
import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	Output,
	Renderer2,
	ViewChild
} from '@angular/core';

import { Observable } from 'rxjs';

import { TranslateService } from '@codeandweb/ngx-translate';

import { SelectOption } from '../ae-select/ae-select.component';
import { AngularEditorService, UploadResponse } from '../angular-editor.service';
import { CustomClass } from '../config';

@Component({
	selector: 'angular-editor-toolbar, ae-toolbar, div[aeToolbar]',
	templateUrl: './ae-toolbar.component.html',
	styleUrls: ['./ae-toolbar.component.scss'],
	//encapsulation: ViewEncapsulation.None,
})

export class AeToolbarComponent implements AfterViewInit, OnDestroy {

	@ViewChild('linkToolip', {static: true}) linkTooltip: ElementRef<HTMLDivElement>;
	scrollListener: any;

	@Output() onProgramaticEdition: EventEmitter<void> = new EventEmitter();

	htmlMode = false;
	noLinkSelected = false;
	multipleNodeSelected = false;
	block = 'default';
	fontName = 'Times New Roman';
	fontSize = '3';
	foreColour: string;
	backColor: string;

	headings: SelectOption[] = [
		{
			label: 'Heading 1',
			value: 'h1',
		},
		{
			label: 'Heading 2',
			value: 'h2',
		},
		{
			label: 'Heading 3',
			value: 'h3',
		},
		{
			label: 'Heading 4',
			value: 'h4',
		},
		{
			label: 'Heading 5',
			value: 'h5',
		},
		{
			label: 'Heading 6',
			value: 'h6',
		},
		{
			label: 'Paragraph',
			value: 'p',
		},
		// {
		//   label: 'Predefined',
		//   value: 'pre'
		// },
		{
			label: 'Standard',
			value: 'default'
		},
		// {
		//   label: 'default',
		//   value: 'default'
		// }
	];

	fontSizes: SelectOption[] = [
		{
			label: '1',
			value: '1',
		},
		{
			label: '2',
			value: '2',
		},
		{
			label: '3',
			value: '3',
		},
		{
			label: '4',
			value: '4',
		},
		{
			label: '5',
			value: '5',
		},
		{
			label: '6',
			value: '6',
		},
		{
			label: '7',
			value: '7',
		}
	];

	customClassId = '-1';
	// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
	_customClasses: CustomClass[];
	customClassList: SelectOption[] = [{label: '', value: ''}];
	// uploadUrl: string;

	tagMap: {[key: string]: string} = {
		BLOCKQUOTE: 'indent',
		A: 'link'
	};

	select = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'PRE', 'DIV'];

	buttons = ['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', 'justifyLeft', 'justifyCenter',
		'justifyRight', 'justifyFull', 'indent', 'outdent', 'insertUnorderedList', 'insertOrderedList', 'link'];

	@Input() id: string;
	@Input() uploadUrl: string;
	@Input() upload: (file: File) => Observable<HttpEvent<UploadResponse>>;
	@Input() showToolbar: boolean;
	@Input() fonts: SelectOption[] = [{label: '', value: ''}];

	@Input()
	set customClasses(classes: CustomClass[]) {
		if (classes) {
			this._customClasses = classes;
			this.customClassList = this._customClasses.map((x, i) => ({label: x.name, value: i.toString()}));
			this.customClassList.unshift({label: 'Clear Class', value: '-1'});
		}
	}

	@Input()
	set defaultFontName(value: string) {
		if (value) {
			this.fontName = value;
		}
	}

	@Input()
	set defaultFontSize(value: string) {
		if (value) {
			this.fontSize = value;
		}
	}

	@Input() hiddenButtons: string[][];

	@Output() execute: EventEmitter<string> = new EventEmitter<string>();

	@ViewChild('fileInput', {static: true}) myInputFile: ElementRef;

	public get isLinkButtonDisabled(): boolean {
		return this.htmlMode || !Boolean(this.editorService.selectedText) || !this.noLinkSelected;
	}

	constructor(
		private r: Renderer2,
		private translateService: TranslateService,
		private editorService: AngularEditorService,
		private er: ElementRef,
		@Inject(DOCUMENT) private doc: Document,
	) {
	}

	ngAfterViewInit(): void {
		this.scrollListener = () => {
			this.hideLinkTooltip();
		}
		this.doc.addEventListener('scroll', this.scrollListener);
	}

	ngOnDestroy(): void {
		this.doc.removeEventListener('scroll', this.scrollListener);
	}

	showLinkTooltip() {
		this.linkTooltip.nativeElement.classList.add('flex');
	}

	hideLinkTooltip() {
		this.linkTooltip.nativeElement.classList.remove('flex');
	}

	/**
	 * Trigger command from editor header buttons
	 * @param command string from toolbar buttons
	 */
	triggerCommand(command: string) {
		this.execute.emit(command);
	}

	/**
	 * highlight editor buttons when cursor moved or positioning
	 */
	triggerButtons() {
		if (!this.showToolbar) {
			return;
		}
		this.buttons.forEach(e => {
			const result = this.doc.queryCommandState(e);
			// Exemple: doc.queryCommandState('bold') détermine si la sélection est en gras.
			// Si on est dans un texte en gras (plage de taille nulle ou pas) le résultat sera
			// vrai, cependant ça n'a pas l'air de marcher si on double clique sur un mot.

			const elementById = this.doc.getElementById(e + '-' + this.id);
			if (result) {
				this.r.addClass(elementById, 'active');
			} else {
				this.r.removeClass(elementById, 'active');
			}
		});
	}

	/**
	 * trigger highlight editor buttons when cursor moved or positioning in block
	 */
	triggerBlocks(userSelection: Selection) {
		let range = userSelection.getRangeAt(0);

		this.multipleNodeSelected = userSelection.anchorNode != null && userSelection.focusNode != null && userSelection.anchorNode != userSelection.focusNode;
		let container: Node | null = range.startContainer;
		this.noLinkSelected = true;

		while (container instanceof Element) {
			let anchor = container.querySelector('a')
			if (anchor) {
				this.noLinkSelected = false;
				break;
			}
			container = container.nextElementSibling;
		}

		// Récupération de la hiérarchie des noeuds
		let a: Node|null = userSelection.focusNode;
		const nodes: Node[] = [];
		while (a && ( !(a instanceof HTMLDivElement) || !a.classList.contains('angular-editor-textarea'))) {
			nodes.unshift(a);
			a = a.parentNode;
		}

		if (!this.showToolbar) {
			return;
		}

		let found = false;
		this.select.forEach(y => {
			const node = nodes.find(x => x.nodeName === y);
			if (node !== undefined && y === node.nodeName) {
				if (found === false) {
					this.block = node.nodeName.toLowerCase();
					found = true;
				}
			} else if (found === false) {
				this.block = 'default';
			}
		});

		found = false;
		if (this._customClasses) {
			this._customClasses.forEach((y, index) => {
				const node = nodes.find((x: any) => {
					if (x instanceof Element) {
						return x.className === y.class;
					}
					return false;
				});
				if (node !== undefined) {
					if (found === false) {
						this.customClassId = index.toString();
						found = true;
					}
				} else if (found === false) {
					this.customClassId = '-1';
				}
			});
		}

		// Activation/désactivation de certaines fonctinnalités selon la sélection

		Object.keys(this.tagMap).map(e => {
			//@ts-ignore
			const elementById = this.doc.getElementById(this.tagMap[e] + '-' + this.id);
			const node = nodes.find(x => x.nodeName === e);

			let activate = false;

			if (node !== undefined && e === node.nodeName) {
				if (node.nodeName == 'A') {
					activate = !this.multipleNodeSelected && !this.noLinkSelected;
				}
				else {
					activate = true;
				}
			}

			if (activate) {
				this.r.addClass(elementById, 'active');
			}
			else {
				this.r.removeClass(elementById, 'active');
			}
		});

		this.foreColour = this.doc.queryCommandValue('ForeColor');
		this.fontSize = this.doc.queryCommandValue('FontSize');
		this.fontName = this.doc.queryCommandValue('FontName').replace(/"/g, '');
		this.backColor = this.doc.queryCommandValue('backColor');

		// Affichage d'un toolip si on sélectionne un seul lien

		let linkTooltip = this.linkTooltip.nativeElement;
		linkTooltip.classList.remove('flex');

		//@ts-ignore
		linkTooltip['anchor'] = null;

		if (range.startContainer && range.endContainer == range.startContainer) {
			let anchor = this.getClosestAnchorInHieararchy(range.startContainer)!;

			// Si on sélectionne bien à l'intérieur d'un lien
			if (anchor instanceof HTMLAnchorElement && range.startContainer.textContent && range.startOffset < range.startContainer.textContent.length) {
				let anchorRect = anchor.getBoundingClientRect();

				this.showLinkTooltip();
				linkTooltip.style.top = (anchorRect.bottom + 5) + 'px';
				linkTooltip.style.left = anchorRect.left + 'px';

				//@ts-ignore
				linkTooltip['anchor'] = anchor;

				this.editorService.forceSelection(anchor);
			}
		}
	}

	/**
	 * insert URL link
	 */
	insertUrl() {
		let url: string | null = 'https:\/\/';
		const selection = this.editorService.savedSelection;
		if (
			selection
			&& selection.startContainer.parentElement
			&& selection.startContainer.parentElement.nodeName === 'A'
		) {
			// force the whole link node selection
			this.editorService.forceSelection(selection.startContainer.parentElement);

			const parent = selection.startContainer.parentElement as HTMLAnchorElement;
			if (parent.href !== '') {
				url = parent.href;
			}
		}

		this.hideLinkTooltip();

		url = prompt(this.translateService.instant('editor.insert_link'), url);
		if (url && url !== '' && url !== 'https://') {
			this.editorService.createLink(url);
		}
	}

	/**
	 * Edit a URL link
	 */
	editURL() {
		this.hideLinkTooltip();

		//@ts-ignore
		let anchor: HTMLAnchorElement = this.linkTooltip.nativeElement['anchor'];

		if (!anchor) {
			return;
		}

		let newURL = prompt(this.translateService.instant('editor.edit_link'), anchor.href);
		if (newURL) {
			anchor.href = newURL;
			this.editorService.forceSelection(anchor);
			this.editorService.executeCommand('insertHTML', anchor.outerHTML);
		}
	}

	removeURL() {
		this.hideLinkTooltip();

		//@ts-ignore
		let anchor: HTMLAnchorElement = this.linkTooltip.nativeElement['anchor'];

		if (!anchor) {
			return;
		}

		// Remplacement du lien par du texte
		anchor.insertAdjacentText('beforebegin', anchor.text);
		anchor.remove();
		this.onProgramaticEdition.emit();
	}

	getClosestAnchorInHieararchy(node: Node): HTMLAnchorElement | undefined {

		let current: Node|null = node;
		while (current && (current.parentElement && current.nodeName != 'A')) {
			current = current?.parentElement;
		}

		if (current?.nodeName != 'A') {
			return;
		}

		return current as HTMLAnchorElement;
	}

	/**
	 * insert Video link
	 */
	insertVideo() {
		this.execute.emit('');
		const url = prompt('Insert Video link', `https://`);
		if (url && url !== '' && url !== `https://`) {
			this.editorService.insertVideo(url);
		}
	}

	/** insert color */
	insertColor(color: string, where: string) {
		this.editorService.insertColor(color, where);
		this.execute.emit('');
	}

	/**
	 * set font Name/family
	 * @param foreColor string
	 */
	setFontName(foreColor: string): void {
		this.editorService.setFontName(foreColor);
		this.execute.emit('');
	}

	/**
	 * set font Size
	 * @param fontSize string
	 */
	setFontSize(fontSize: string): void {
		this.editorService.setFontSize(fontSize);
		this.execute.emit('');
	}

	/**
	 * toggle editor mode (WYSIWYG or SOURCE)
	 * @param m boolean
	 */
	setEditorMode(m: boolean) {
		const toggleEditorModeButton = this.doc.getElementById('toggleEditorMode' + '-' + this.id);
		if (m) {
			this.r.addClass(toggleEditorModeButton, 'active');
		} else {
			this.r.removeClass(toggleEditorModeButton, 'active');
		}
		this.htmlMode = m;
	}

	/**
	 * Upload image when file is selected.
	 */
	onFileChanged(event: any) {
		const file = event.target.files[0];
		if (file.type.includes('image/')) {
			if (this.upload) {
				this.upload(file).subscribe((response: HttpResponse<UploadResponse>) => this.watchUploadImage(response, event));
			} else if (this.uploadUrl) {
				this.editorService.uploadImage(file).subscribe((response: HttpResponse<UploadResponse>) => this.watchUploadImage(response, event));
			} else {
				const reader = new FileReader();
				reader.onload = (e: ProgressEvent) => {
					const fr = e.currentTarget as FileReader;
					this.editorService.insertImage(fr.result!.toString());
				};
				reader.readAsDataURL(file);
			}
		}
	}

	watchUploadImage(response: HttpResponse<{ imageUrl: string }>, event: any) {
		const {imageUrl} = response.body!;
		this.editorService.insertImage(imageUrl);
		event.srcElement.value = null;
	}

	/**
	 * Set custom class
	 */
	setCustomClass(classId: string) {
		if (classId === '-1') {
			this.execute.emit('clear');
		} else {
			this.editorService.createCustomClass(this._customClasses[+classId]);
		}
	}

	isButtonHidden(name: string): boolean {
		if (!name) {
			return false;
		}
		if (!(this.hiddenButtons instanceof Array)) {
			return false;
		}
		let result: any;
		for (const arr of this.hiddenButtons) {
			if (arr instanceof Array) {
				result = arr.find(item => item === name);
			}
			if (result) {
				break;
			}
		}
		return result !== undefined;
	}

	focus() {
		this.execute.emit('focus');
	}
}
